import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { FaFacebook, FaWhatsapp, FaTimes } from "react-icons/fa";
import { ThumbsUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { IoIosShareAlt } from "react-icons/io";
import { useAuth } from "../context/AuthContext";
import GoogleLoginButton from "./GoogleLoginButton";
import "../styles/main.css";

const RelatedArticleItem = ({ article, currentCategory, currentArticleId }) => {
  const [expanded, setExpanded] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [loadingLike, setLoadingLike] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const { googleLogin, currentUser } = useAuth();
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  useEffect(() => {
    const fetchLikeStatus = async () => {
      if (!token) return;
      const storedUser = JSON.parse(localStorage.getItem("user"));
      const userId = storedUser?.id;
      try {
        const res = await fetch(
          `https://api.todaytalks.in/api/likes/${article.category}/${article._id}/like-status?userId=${userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.ok) {
          const data = await res.json();
          setLiked(data.liked);
          setLikesCount(data.likesCount);
        }
      } catch (error) {
        console.error("Error fetching like status:", error);
      }
    };

    fetchLikeStatus();
  }, [article._id, article.category, token]);

  const handleLike = async (e) => {
    e.stopPropagation();
    const token = localStorage.getItem("token");
    if (!token) {
      setShowLoginModal(true);
      return;
    }
    const storedUser = JSON.parse(localStorage.getItem("user"));
    const userId = storedUser?.id;
    if (!userId) {
      setShowLoginModal(true);
      return;
    }
    setLoadingLike(true);
    try {
      const res = await fetch(
        `https://api.todaytalks.in/api/likes/${article.category}/${article._id}/like`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId }),
        }
      );

      if (res.ok) {
        const data = await res.json();
        setLiked(data.liked);
        setLikesCount(data.likesCount);
      }
    } catch (error) {
      console.error("Error liking article:", error);
    } finally {
      setLoadingLike(false);
    }
  };

  const paragraphs =
    article.description?.split("\n").filter((p) => p.trim()) || [];
  const previewParagraphs = paragraphs.slice(0, 1);
  const remainingParagraphs = paragraphs.slice(2);
  const toggleShareOptions = (e) => {
    e.stopPropagation();
    setShowShareOptions(!showShareOptions);
  };
  const shareOnSocialMedia = (platform, e) => {
    e.stopPropagation();
    const articleUrl = `${window.location.origin}/article/${article._id}`;
    const shareText = `🔷 *${article.title}* 🔷\n\n${
      article.summary || ""
    }\n\nRead more: ${articleUrl}`;
    switch (platform) {
      case "facebook":
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            articleUrl
          )}`,
          "_blank"
        );
        break;
      case "whatsapp":
        window.open(
          `https://wa.me/?text=${encodeURIComponent(shareText)}`,
          "_blank"
        );
        break;
      default:
        break;
    }
    setShowShareOptions(false);
  };

  const handleArticleClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => {
      navigate(`/article/${article._id}`, {
        state: {
          returnPath: window.location.pathname,
          scrollPosition: window.scrollY,
          currentCategory: currentCategory,
          articleId: currentArticleId,
        },
      });
    }, 300);
  };

  const handleGoogleSuccess = async (credential) => {
    try {
      setError("");
      setLoading(true);
      const result = await googleLogin(credential);
      if (result.success) {
        window.location.reload();
        setTimeout(() => {
          handleLike({ stopPropagation: () => {} });
        }, 500);
        window.location.reload();
      } else {
        setError(result.message || "Google authentication failed");
      }
    } catch (error) {
      setError("Google authentication error");
      window.location.reload();
      console.error("Google auth error:", error);
    } finally {
      setLoading(false);
      window.location.reload();
    }
  };

  const handleGoogleError = (errorMessage) => {
    setError(errorMessage || "Google sign-in failed. Please try again.");
    window.location.reload();
  };

  const handleEmailLogin = () => {
    setShowLoginModal(false);
    navigate("/login", {
      state: {
        returnTo: window.location.pathname,
        articleId: article._id,
      },
    });
  };

  return (
    <>
      <article
        className="related-article-main"
        onClick={handleArticleClick}
        style={{
          boxShadow: "var(--box-shadow) 0px 2px 8px 0px",
          backgroundColor: "var(--card-bg)",
        }}
      >
        <header className="article-headerra">
          <div className="article-header-top">
            <div className="article-category">{article.category}</div>
            <div style={{ display: "none", alignItems: "center", gap: "10px" }}>
              <button
                className="like-button"
                onClick={handleLike}
                disabled={loadingLike}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "22px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <motion.span
                  key={liked} 
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1.2 }}
                  transition={{ type: "spring", stiffness: 300, damping: 10 }}
                >
                  {liked ? (
                    <ThumbsUp style={{ color: "#0d6efd" }} />
                  ) : (
                    <ThumbsUp style={{ color: "gray" }} />
                  )}
                </motion.span>
                <span>{likesCount}</span>
              </button>
              <div className="article-share-container">
                <button
                  className="share-button"
                  onClick={toggleShareOptions}
                  aria-label="Share article"
                >
                  <IoIosShareAlt />
                </button>
                {showShareOptions && (
                  <div
                    className="share-optionsrel"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      title="Share on Facebook"
                      onClick={(e) => shareOnSocialMedia("facebook", e)}
                    >
                      <FaFacebook color="#3b5998" size={30} />
                    </button>
                    <button onClick={(e) => shareOnSocialMedia("whatsapp", e)}>
                      <FaWhatsapp color="#25D366" size={30} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="article-featured-image">
            <img
              src={article.imageUrl}
              alt={article.title}
              onError={(e) => {
                e.target.src = "/default-news-image.jpg";
                e.target.alt = "Default news image";
              }}
            />
            {article.imageCaption && (
              <figcaption className="image-caption">
                {article.imageCaption}
              </figcaption>
            )}
          </div>
          <h2 className="article-title">{article.title}</h2>
          <div className="article-meta">
            <span className="publication-date">
              {article.createdAt || article.publishedAt
                ? format(
                    new Date(article.createdAt || article.publishedAt),
                    "MMMM d, yyyy"
                  )
                : "Date not available"}
            </span>
            {article.author && (
              <span className="article-author">By {article.author}</span>
            )}
            {article.trending && (
              <span className="trending-badge">🔥 Trending</span>
            )}
          </div>
          {remainingParagraphs.length > 0 && (
            <button className="read-more-button" onClick={handleArticleClick}>
              {expanded ? "Read Less" : "Read More"}
            </button>
          )}
        </header>

        {article.sourceUrl && (
          <div className="article-source">
            <h4>Source:</h4>
            <a
              href={article.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="source-link"
              onClick={(e) => e.stopPropagation()}
            >
              {new URL(article.sourceUrl).hostname}
            </a>
          </div>
        )}
      </article>
      <AnimatePresence>
        {showLoginModal && (
          <>
            <motion.div
              className="modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-end",
                zIndex: 1000,
              }}
              onClick={() => setShowLoginModal(false)}
            >
              <motion.div
                className="modal-content"
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                style={{
                  backgroundColor: "#1e293b",
                  padding: "20px",
                  borderTopLeftRadius: "6px",
                  borderTopRightRadius: "6px",
                  width: "100%",
                  maxWidth: "500px",
                  position: "relative",
                  maxHeight: "80vh",
                  overflowY: "auto",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setShowLoginModal(false)}
                  style={{
                    position: "absolute",
                    top: "15px",
                    right: "15px",
                    background: "none",
                    border: "none",
                    fontSize: "20px",
                    cursor: "pointer",
                    color: "white",
                  }}
                >
                  <FaTimes />
                </button>

                <h3
                  style={{
                    marginBottom: "15px",
                    textAlign: "center",
                    color: "white",
                    fontSize: "1.2rem",
                  }}
                >
                  Login to proceed
                </h3>

                {error && (
                  <div
                    className="error-message"
                    style={{
                      color: "#ff6b6b",
                      marginBottom: "15px",
                      textAlign: "center",
                      padding: "8px",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      borderRadius: "4px",
                    }}
                  >
                    {error}
                  </div>
                )}

                <div className="oauth-section">
                  <GoogleLoginButton
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    text="Login with Google"
                  />

                  <div
                    className="divider"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      margin: "15px 0",
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        height: "1px",
                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                      }}
                    ></div>
                    <span
                      style={{
                        padding: "0 10px",
                        color: "rgba(255, 255, 255, 0.7)",
                      }}
                    >
                      or
                    </span>
                    <div
                      style={{
                        flex: 1,
                        height: "1px",
                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                      }}
                    ></div>
                  </div>

                  <button
                    onClick={handleEmailLogin}
                    style={{
                      width: "100%",
                      padding: "12px",
                      color: "rgb(5, 65, 69)",
                      backgroundColor: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "16px",
                      fontWeight: "500",
                      marginBottom: "10px",
                    }}
                  >
                    Use Email & Password
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default RelatedArticleItem;
